import { useState, useRef, useEffect } from 'react';
import { MicrophoneIcon, StopIcon, PlayIcon, PauseIcon, TrashIcon } from '@heroicons/react/24/outline';

interface AudioRecorderProps {
  onSave: (blob: Blob, duration: number, category: string) => Promise<void>;
  maxDuration?: number; // seconds
}

export function AudioRecorder({ onSave, maxDuration = 120 }: AudioRecorderProps) {
  const [isRecording, setIsRecording] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [audioBlob, setAudioBlob] = useState<Blob | null>(null);
  const [audioUrl, setAudioUrl] = useState<string>('');
  const [duration, setDuration] = useState(0);
  const [recordingTime, setRecordingTime] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [category, setCategory] = useState<string>('normal_operation');
  const [saving, setSaving] = useState(false);

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      if (audioUrl) URL.revokeObjectURL(audioUrl);
    };
  }, [audioUrl]);

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream, {
        mimeType: 'audio/webm',
      });

      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = () => {
        const blob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        setAudioBlob(blob);
        const url = URL.createObjectURL(blob);
        setAudioUrl(url);

        // Stop all tracks
        stream.getTracks().forEach((track) => track.stop());
      };

      mediaRecorder.start();
      setIsRecording(true);
      setRecordingTime(0);

      // Start timer
      timerRef.current = setInterval(() => {
        setRecordingTime((prev) => {
          const newTime = prev + 1;
          if (newTime >= maxDuration) {
            stopRecording();
          }
          return newTime;
        });
      }, 1000);
    } catch (error) {
      console.error('Failed to start recording:', error);
      alert('Failed to access microphone. Please ensure microphone permissions are granted.');
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      setIsPaused(false);
      setDuration(recordingTime);

      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    }
  };

  const pauseRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.pause();
      setIsPaused(true);
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    }
  };

  const resumeRecording = () => {
    if (mediaRecorderRef.current && isPaused) {
      mediaRecorderRef.current.resume();
      setIsPaused(false);

      timerRef.current = setInterval(() => {
        setRecordingTime((prev) => {
          const newTime = prev + 1;
          if (newTime >= maxDuration) {
            stopRecording();
          }
          return newTime;
        });
      }, 1000);
    }
  };

  const playAudio = () => {
    if (audioRef.current) {
      audioRef.current.play();
      setIsPlaying(true);
    }
  };

  const pauseAudio = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      setIsPlaying(false);
    }
  };

  const deleteRecording = () => {
    if (audioUrl) URL.revokeObjectURL(audioUrl);
    setAudioBlob(null);
    setAudioUrl('');
    setDuration(0);
    setRecordingTime(0);
    setIsPlaying(false);
  };

  const handleSave = async () => {
    if (!audioBlob) return;

    setSaving(true);
    try {
      await onSave(audioBlob, duration, category);
      deleteRecording();
    } catch (error) {
      console.error('Failed to save audio:', error);
      alert('Failed to save audio recording');
    } finally {
      setSaving(false);
    }
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <div className="space-y-4">
      {/* Category Selection */}
      {!isRecording && !audioBlob && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Recording Category
          </label>
          <select
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            className="input w-full"
          >
            <option value="normal_operation">Normal Operation</option>
            <option value="abnormal_noise">Abnormal Noise</option>
            <option value="other">Other</option>
          </select>
        </div>
      )}

      {/* Recording Controls */}
      {!audioBlob && (
        <div className="flex items-center gap-3">
          {!isRecording ? (
            <button
              onClick={startRecording}
              className="btn btn-primary flex items-center"
            >
              <MicrophoneIcon className="w-5 h-5 mr-2" />
              Start Recording
            </button>
          ) : (
            <>
              {!isPaused ? (
                <button
                  onClick={pauseRecording}
                  className="btn btn-secondary flex items-center"
                >
                  <PauseIcon className="w-5 h-5 mr-2" />
                  Pause
                </button>
              ) : (
                <button
                  onClick={resumeRecording}
                  className="btn btn-secondary flex items-center"
                >
                  <PlayIcon className="w-5 h-5 mr-2" />
                  Resume
                </button>
              )}
              <button
                onClick={stopRecording}
                className="btn bg-red-600 hover:bg-red-700 text-white flex items-center"
              >
                <StopIcon className="w-5 h-5 mr-2" />
                Stop
              </button>
              <span className="text-lg font-mono text-gray-700">
                {formatTime(recordingTime)} / {formatTime(maxDuration)}
              </span>
            </>
          )}
        </div>
      )}

      {/* Playback Controls */}
      {audioBlob && (
        <div className="border-2 border-gray-200 rounded-lg p-4 space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-gray-700">
              Recording: {formatTime(duration)}
            </span>
            <span className="text-xs text-gray-500 capitalize">
              {category.replace('_', ' ')}
            </span>
          </div>

          <audio
            ref={audioRef}
            src={audioUrl}
            onEnded={() => setIsPlaying(false)}
            className="hidden"
          />

          <div className="flex items-center gap-3">
            {!isPlaying ? (
              <button
                onClick={playAudio}
                className="btn btn-secondary flex items-center text-sm"
              >
                <PlayIcon className="w-4 h-4 mr-1" />
                Play
              </button>
            ) : (
              <button
                onClick={pauseAudio}
                className="btn btn-secondary flex items-center text-sm"
              >
                <PauseIcon className="w-4 h-4 mr-1" />
                Pause
              </button>
            )}
            <button
              onClick={deleteRecording}
              className="btn bg-red-600 hover:bg-red-700 text-white flex items-center text-sm"
            >
              <TrashIcon className="w-4 h-4 mr-1" />
              Delete
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              className="btn btn-primary text-sm ml-auto"
            >
              {saving ? 'Saving...' : 'Save Recording'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
